import * as React from 'react';
import { AppState, useAppStateContext } from 'src/appState/AppState';
import { EventRowUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowUnified';
import { EventGroupHeaderUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventGroupHeaderUnified';
import {
    getAmericanLeagueRowFlags,
    getAmericanSportLogoStateConstructor,
    renderAmericanLeagueEventRowIfApplicable,
    renderAmericanLeagueGroupHeaderIfApplicable,
    shouldUseUnifiedAmericanSportRow,
} from 'src/domains/sportsbook/webview/components/eventsList/americanSportEventRow';
import {
    EventListWrapper,
    FeaturedSection,
} from 'src/domains/sportsbook/webview/components/eventsList/EventList.style';
import { observer } from 'src/utils/mobx-react';
import {
    EventsGroupViewCompetition,
    EventsGroupViewSport,
} from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupView';
import { EventsGroupState } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupState';
import { getHeadersForMarketTemplate } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/getMarketAndSelections';
import {
    isFightSport,
    resolveUnifiedSelectionIds,
} from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedEventsListMarkets';
import { UnifiedSportEventsListState } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedSportEventsListState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { SpecialSportsState } from 'src/domains/sportsbook/state/specialSportsState/SpecialSportsState';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { Collapse } from 'src/ui/components/collapse/sports/05/Collapse';
import { TranslationsStore } from 'src/domains/layouts/state/translationsStore/TranslationsStore';
import classNames from 'classnames';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { OutrightsCompetitionsList } from 'src/domains/sportsbook/webview/components/sportEventsListNew/OutrightsCompetitionsList';
import { EventsTableMarketsLoader } from 'src/domains/sportsbook/shared/Components';
import { InfoIconUnifiedWrapper } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsTableMobX.style';
import { InfoIconUnified } from 'src/domains/layouts/shared/assets/icons/InfoIconUnified';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { SectionHeader } from 'src/ui/components/collapse/sports/05/SectionHeader';
import { SportCompetitionsList } from 'src/domains/sportsbook/webview/components/sportEventsListNew/SportCompetitionsList/SportCompetitionsList';
import { shouldShowOutrightsFilter } from 'src/domains/sportsbook/webview/components/sportEventsListNew/SportsFilters/UnifiedSportsFilters.state';
import { EventsTableInfo } from './EventGroupHeaderUnified.style';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { AmericanLeagueRowFlagsType } from 'src/domains/sportsbook/webview/components/eventsList/americanSportEventRow';
import {
    EVENTS_PER_PAGE,
    isGolf,
    useExpandedEvents,
} from 'src/domains/sportsbook/webview/components/sportEventsListNew/utils';
import { isOutrightEventItem } from 'src/domains/sportsbook/utils/isOutrightEvent';
import { EventRowOutrightsUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowOutrightsUnified';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
const DEFAULT_OPEN_GROUPS = 5;

export enum FeaturedSectionType {
    MATCHES = 'matches',
    COMPETITIONS = 'competitions',
    INPLAY = 'inplay',
    OUTRIGHTS = 'outrights',
}
const renderEvent = ({
    event,
    sportId,
    competitionId,
    selectionIds,
    eventsListState,
    geniusSports,
    flags,
    showOutrightsEventRow,
}: {
    event: EventListGroupEventItemType;
    sportId: string;
    competitionId: CompetitionId | null;
    selectionIds: Array<string>;
    eventsListState: UnifiedSportEventsListState;
    geniusSports: ReadonlyArray<string>;
    flags: AmericanLeagueRowFlagsType;
    showOutrightsEventRow: boolean;
}): React.ReactNode => {
    const useUnifiedAmericanRow = shouldUseUnifiedAmericanSportRow(flags, sportId, geniusSports);
    const americanRow = useUnifiedAmericanRow
        ? null
        : renderAmericanLeagueEventRowIfApplicable(event, flags, 'event_unified');
    const rowStateConstructor = useUnifiedAmericanRow ? getAmericanSportLogoStateConstructor(flags, event) : null;

    if (americanRow !== null) {
        return americanRow;
    }

    if (isOutrightEventItem(event) && showOutrightsEventRow) {
        return (
            <EventRowOutrightsUnified
                key={`event_${event.id}`}
                smallEvent={event}
            />
        );
    }

    return (
        <EventRowUnified
            key={`event_unified_${event.id}`}
            smallEvent={event}
            sportId={sportId}
            selectionsHeaders={selectionIds}
            competitionId={competitionId}
            eventsListState={eventsListState}
            rowStateConstructor={rowStateConstructor}
        />
    );
};

const renderCompetition = ({
    competitionId,
    competitionGroup,
    selectionIds,
    eventsListState,
    geniusSports,
    limit,
}: {
    competitionId: CompetitionId;
    competitionGroup: EventsGroupViewCompetition;
    selectionIds: Array<string>;
    eventsListState: UnifiedSportEventsListState;
    geniusSports: ReadonlyArray<string>;
    limit?: number;
}): Array<React.ReactNode> => {
    const { sport: sportId, name, events: allEvents } = competitionGroup;
    const events = limit !== undefined ? allEvents.slice(0, limit) : allEvents;
    const flags = getAmericanLeagueRowFlags(name, sportId);
    const showOutrightsEventRow = eventsListState.filter_outrights === false;

    return events.map((event) =>
        renderEvent({
            event,
            sportId,
            competitionId,
            selectionIds,
            eventsListState,
            geniusSports,
            flags,
            showOutrightsEventRow,
        })
    );
};
const competitionHasEvents = (events: EventsGroupViewCompetition['events']): boolean => {
    return (
        events.length > 0 &&
        events.every((event) => {
            const eventModel = event.id2.getEventModel();

            return eventModel === null || eventModel.display === true;
        })
    );
};

const SportGroup = ({
    sportGroup,
    competitionNodes,
    showHeaderSport,
}: {
    sportGroup: EventsGroupViewSport;
    competitionNodes: React.ReactNode;
    showHeaderSport?: boolean;
}) => {
    const common = useCommon();
    const translationsStore = TranslationsStore.get(common);
    const [isOpen, setIsOpen] = React.useState(sportGroup.visible);

    return (
        <>
            {showHeaderSport ? (
                <>
                    <Collapse
                        dataTest='header-sportLevel'
                        handleToggle={() => setIsOpen((prev) => !prev)}
                        isOpen={isOpen}
                        title={translationsStore.translateSport(sportGroup.id)}
                    />
                    {isOpen ? competitionNodes : null}
                </>
            ) : (
                competitionNodes
            )}
        </>
    );
};

interface EventsListByDatePropsType {
    sportGroup: EventsGroupViewSport;
    eventsListState: UnifiedSportEventsListState;
    preferCompetitionSelectionsHeadersOnly: boolean;
}

const EventsListByDate = observer(
    'EventsListByDate',
    ({ sportGroup, eventsListState, preferCompetitionSelectionsHeadersOnly }: EventsListByDatePropsType) => {
        const common = useCommon();
        const { geniusSports } = ConfigComponents.get(common).config;
        const competition = sportGroup.competitions[0];

        if (competition === undefined) return null;

        const { events, eventsByDate, possibleMarketAndSelectionInListHeaders: fallbackHeaders } = competition;

        const { id: sportId } = sportGroup;
        const {
            filter_outrights: showOutrightsCompetitionsList,
            filter_marketTemplateId: filterTemplateId,
            lastSelectionsHeader: tabLastSelectionsHeader,
            lastSelections: activeFilterSelectionIds,
        } = eventsListState;

        const competitionId = common.models.id.getCompetitionId(competition.id);

        const filterSelectionIds =
            filterTemplateId !== null ? getHeadersForMarketTemplate(events, filterTemplateId) : [];

        const selectionIds = resolveUnifiedSelectionIds({
            sportId,
            filterTemplateId,
            filterSelectionIds,
            activeFilterSelectionIds,
            fallbackHeaders,
        });

        const selectionsHeader =
            preferCompetitionSelectionsHeadersOnly === true || isFightSport(sportId)
                ? eventsListState.translateSelectionHeaders(selectionIds)
                : filterTemplateId !== null
                  ? filterSelectionIds.length > 0
                      ? competition.translateSelectionHeaders(filterSelectionIds)
                      : tabLastSelectionsHeader
                  : tabLastSelectionsHeader.length > 0
                    ? tabLastSelectionsHeader
                    : eventsListState.translateSelectionHeaders(selectionIds);

        const flags = getAmericanLeagueRowFlags(competition.name, competition.sport);
        const useUnifiedAmericanRow = shouldUseUnifiedAmericanSportRow(flags, sportGroup.id, geniusSports);

        return (
            <>
                {eventsByDate.map((dateView) => {
                    const { date, visible, events, toggle } = dateView;
                    const renderEvents = events.map((event) =>
                        renderEvent({
                            event,
                            sportId,
                            competitionId,
                            selectionIds,
                            eventsListState,
                            geniusSports,
                            flags,
                            showOutrightsEventRow: true,
                        })
                    );

                    const americanHeader = useUnifiedAmericanRow
                        ? null
                        : renderAmericanLeagueGroupHeaderIfApplicable({
                              competitionName: competition.name,
                              sport: competition.sport,
                              areEventsTwoParticipantsOnly: competition.areEventsTwoParticipantsOnly,
                              dataTest: 'date-header-unified',
                              label: date,
                              onToggle: toggle,
                              open: visible,
                          });

                    return (
                        <React.Fragment key={`group_unified_${date}`}>
                            {!showOutrightsCompetitionsList
                                ? americanHeader ?? (
                                      <EventGroupHeaderUnified
                                          dataTest='date-header-unified'
                                          label={date}
                                          onToggle={toggle}
                                          open={visible}
                                          selections={selectionsHeader}
                                          sportId={sportGroup.id}
                                      />
                                  )
                                : null}

                            {visible ? renderEvents : null}
                        </React.Fragment>
                    );
                })}
            </>
        );
    }
);

const EventsListByDateAcrossCompetitions = observer(
    'EventsListByDateAcrossCompetitions',
    ({ sportGroup, eventsListState, preferCompetitionSelectionsHeadersOnly }: EventsListByDatePropsType) => {
        const common = useCommon();
        const { geniusSports } = ConfigComponents.get(common).config;
        const {
            eventsByDate,
            eventCompetitionByEventId,
            possibleMarketAndSelectionInListHeaders: fallbackHeaders,
            translateSelectionHeaders,
            id: sportId,
            allEvents,
        } = sportGroup;

        const {
            filter_marketTemplateId: filterTemplateId,
            lastSelectionsHeader: tabLastSelectionsHeader,
            lastSelections: activeFilterSelectionIds,
        } = eventsListState;

        const { getVisibleCount, handleShowMore } = useExpandedEvents<string>();

        if (eventsByDate.length === 0) {
            return null;
        }

        const filterSelectionIds =
            filterTemplateId !== null ? getHeadersForMarketTemplate(allEvents, filterTemplateId) : [];

        const selectionIds = resolveUnifiedSelectionIds({
            sportId,
            filterTemplateId,
            filterSelectionIds,
            activeFilterSelectionIds,
            fallbackHeaders,
        });

        const selectionsHeader =
            preferCompetitionSelectionsHeadersOnly === true || isFightSport(sportId)
                ? eventsListState.translateSelectionHeaders(selectionIds)
                : filterTemplateId !== null
                  ? filterSelectionIds.length > 0
                      ? translateSelectionHeaders(filterSelectionIds)
                      : tabLastSelectionsHeader
                  : tabLastSelectionsHeader.length > 0
                    ? tabLastSelectionsHeader
                    : eventsListState.translateSelectionHeaders(selectionIds);

        return (
            <>
                {eventsByDate.map((dateView) => {
                    const { date, visible, events, toggle } = dateView;
                    const visibleCount = getVisibleCount(date);
                    const visibleEvents = events.slice(0, visibleCount);
                    const hasMore = events.length > EVENTS_PER_PAGE;

                    const renderEvents = visibleEvents.map((event) => {
                        const competition = eventCompetitionByEventId.get(event.id);
                        const competitionId =
                            competition !== undefined ? common.models.id.getCompetitionId(competition.id) : null;

                        const flags = getAmericanLeagueRowFlags(
                            competition !== undefined ? competition.name : '',
                            sportId
                        );

                        return renderEvent({
                            event,
                            sportId,
                            competitionId,
                            selectionIds,
                            eventsListState,
                            geniusSports,
                            flags,
                            showOutrightsEventRow: true,
                        });
                    });

                    const showMoreButton = hasMore ? (
                        <ExpandButton
                            isExpanded={events.length === visibleCount}
                            toggleExpand={() => handleShowMore(date, events.length)}
                        />
                    ) : null;

                    const dateHeader = (
                        <EventGroupHeaderUnified
                            dataTest='date-header-unified'
                            label={date}
                            onToggle={toggle}
                            open={visible}
                            selections={selectionsHeader}
                            sportId={sportId}
                        />
                    );

                    return (
                        <React.Fragment key={`group_unified_${date}`}>
                            {dateHeader}

                            {visible ? (
                                <>
                                    {renderEvents}
                                    {showMoreButton}
                                </>
                            ) : null}
                        </React.Fragment>
                    );
                })}
            </>
        );
    }
);

const CompetitionsList = observer(
    'CompetitionsList',
    ({ sportGroup, eventsListState, preferCompetitionSelectionsHeadersOnly }: EventsListByDatePropsType) => {
        const common = useCommon();
        const { geniusSports } = ConfigComponents.get(common).config;
        const rowSportId = sportGroup.id;
        const {
            filter_outrights: showOutrightsCompetitionsList,
            filter_marketTemplateId: filterTemplateId,
            filter_time,
            lastSelectionsHeader: tabLastSelectionsHeader,
            lastSelections: activeFilterSelectionIds,
        } = eventsListState;

        const { getVisibleCount, handleShowMore } = useExpandedEvents<number>();

        return (
            <div
                data-testid='competitions-list'
                className={classNames('competitions-list', `${rowSportId}-competitions-list`)}
            >
                {sportGroup.competitions.map((competition) => {
                    const {
                        id,
                        events,
                        name: competitionName,
                        visible: isVisible,
                        possibleMarketAndSelectionInListHeaders: fallbackHeaders,
                        areEventsTwoParticipantsOnly,
                    } = competition;

                    if (!competitionHasEvents(events)) return null;

                    const eventsLength = events.length;

                    const firstEvent = events[0] ?? null;
                    const filterSelectionIds =
                        filterTemplateId !== null
                            ? getHeadersForMarketTemplate(events.slice(0, 2), filterTemplateId)
                            : [];

                    const selectionIds = resolveUnifiedSelectionIds({
                        sportId: rowSportId,
                        filterTemplateId,
                        filterSelectionIds,
                        activeFilterSelectionIds,
                        fallbackHeaders,
                    });

                    const competitionData = {
                        competitionGroup: competition,
                        competitionId: common.models.id.getCompetitionId(id),
                        selectionIds,
                        eventsListState,
                        geniusSports,
                    };

                    if (isOutrightEventItem(firstEvent) && showOutrightsCompetitionsList) {
                        return (
                            <OutrightsCompetitionsList
                                competition={competition}
                                sportId={rowSportId}
                                eventsListState={eventsListState}
                                key={id}
                            >
                                {renderCompetition(competitionData)}
                            </OutrightsCompetitionsList>
                        );
                    }

                    const selectionsHeader =
                        preferCompetitionSelectionsHeadersOnly === true || isFightSport(rowSportId)
                            ? eventsListState.translateSelectionHeaders(selectionIds)
                            : filterTemplateId !== null
                              ? filterSelectionIds.length > 0
                                  ? eventsListState.translateSelectionHeaders(filterSelectionIds)
                                  : tabLastSelectionsHeader
                              : tabLastSelectionsHeader.length > 0
                                ? tabLastSelectionsHeader
                                : eventsListState.translateSelectionHeaders(selectionIds);

                    const competitionHeaderKey = `group_unified_${id}`;

                    const visibleCount = getVisibleCount(id);
                    const hasMore = eventsLength >= visibleCount && eventsLength > EVENTS_PER_PAGE;

                    const showMoreButton = hasMore ? (
                        <ExpandButton
                            isExpanded={eventsLength === visibleCount}
                            toggleExpand={() => handleShowMore(id, eventsLength)}
                        />
                    ) : null;

                    const americanHeader = shouldUseUnifiedAmericanSportRow(
                        getAmericanLeagueRowFlags(competitionName, rowSportId),
                        rowSportId,
                        geniusSports
                    )
                        ? null
                        : renderAmericanLeagueGroupHeaderIfApplicable({
                              competitionName,
                              sport: rowSportId,
                              areEventsTwoParticipantsOnly,
                              dataTest: 'competition-header-unified',
                              label: competitionName,
                              onToggle: competition.toggle,
                              open: isVisible,
                          });

                    return (
                        <div
                            data-testid={`competition-${id}`}
                            className='competition-wrapper'
                            key={id}
                        >
                            {americanHeader ?? (
                                <EventGroupHeaderUnified
                                    dataTest='competition-header-unified'
                                    key={competitionHeaderKey}
                                    label={competitionName}
                                    onToggle={competition.toggle}
                                    open={isVisible}
                                    selections={selectionsHeader}
                                    showCompetitonLabel={filter_time === null}
                                    sportId={rowSportId}
                                    showHeader={!isGolf(rowSportId)}
                                />
                            )}

                            {isVisible && (
                                <div>
                                    {renderCompetition({
                                        ...competitionData,
                                        limit: visibleCount,
                                    })}
                                    {showMoreButton}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
);

const renderSportGroup = (
    sportGroup: EventsGroupViewSport,
    eventsListState: UnifiedSportEventsListState,
    preferCompetitionSelectionsHeadersOnly: boolean = false,
    isCompetitionPage: boolean = false
): React.ReactElement => {
    if (isCompetitionPage) {
        return (
            <EventsListByDate
                sportGroup={sportGroup}
                eventsListState={eventsListState}
                preferCompetitionSelectionsHeadersOnly={preferCompetitionSelectionsHeadersOnly}
            />
        );
    }

    if (eventsListState.filter_time === 'all') {
        return (
            <EventsListByDateAcrossCompetitions
                sportGroup={sportGroup}
                eventsListState={eventsListState}
                preferCompetitionSelectionsHeadersOnly={preferCompetitionSelectionsHeadersOnly}
            />
        );
    }

    return (
        <CompetitionsList
            sportGroup={sportGroup}
            eventsListState={eventsListState}
            preferCompetitionSelectionsHeadersOnly={preferCompetitionSelectionsHeadersOnly}
        />
    );
};

interface StatusPropsType {
    section?: string;
    eventsListState: UnifiedSportEventsListState;
}

const Status = observer('Status', (props: StatusPropsType) => {
    const { section, eventsListState } = props;
    const { inPlayCount, outrightsCount, filter_inPlay, filter_outrights, isLoading } = eventsListState;

    if (isLoading) {
        return <EventsTableMarketsLoader />;
    }

    const renderContent = () => {
        if (filter_inPlay || (inPlayCount === 0 && section === 'inplay')) {
            return (
                <I18n
                    langKey='events.table.empty-in-play'
                    defaultText='No in play Available'
                />
            );
        }

        if (filter_outrights || (outrightsCount === 0 && section === 'outrights')) {
            return (
                <I18n
                    langKey='events.table.empty-outrights'
                    defaultText='No outrights Available'
                />
            );
        }

        return (
            !filter_inPlay &&
            !filter_outrights && (
                <I18n
                    langKey='events.table.empty-unified'
                    defaultText="Sorry, we haven't found any events with such criteria."
                />
            )
        );
    };

    return (
        <EventsTableInfo>
            <InfoIconUnifiedWrapper>
                <InfoIconUnified />
            </InfoIconUnifiedWrapper>

            {renderContent()}
        </EventsTableInfo>
    );
});

class State {
    public readonly appState: AppState;
    public readonly groupEvents: MobxMapAutoNew<EventsCollectionQueryModel | null, EventsGroupState>;

    public constructor(appState: AppState, specialSportsState: SpecialSportsState | null, allGroupsOpen: boolean) {
        this.appState = appState;

        this.groupEvents = new MobxMapAutoNew((eventsCollection) => {
            const { languagesState } = this.appState;

            return new EventsGroupState(
                allGroupsOpen === true ? 'all-open' : DEFAULT_OPEN_GROUPS,
                languagesState.getTranslation,
                eventsCollection,
                specialSportsState
            );
        });
    }
}

interface EventsListUnifiedPropsType {
    allGroupsOpen?: boolean;
    eventsListState: UnifiedSportEventsListState;
    sportId: string;
    showHeaderSport?: boolean;
    eventsCollectionQueryOverride?: EventsCollectionQueryModel | null;
    groupingSpecialSports?: SpecialSportsState | null;
    preferCompetitionSelectionsHeadersOnly?: boolean;
}

const EventsListUnifiedContent = observer('EventsListUnifiedContent', (props: EventsListUnifiedPropsType) => {
    const appState = useAppStateContext();
    const common = useCommon();
    const { currentView } = StarRouter.get(common);
    const page = currentView?.name;

    const { appSportsBookState } = appState;
    const resolvedSpecialSports =
        props.groupingSpecialSports !== undefined
            ? props.groupingSpecialSports
            : appSportsBookState.specialSportsMap.get(props.sportId);

    const [state] = React.useState(() => new State(appState, resolvedSpecialSports, props.allGroupsOpen === true));

    const getListToView = (section?: FeaturedSectionType) => {
        let eventsQuery = props.eventsCollectionQueryOverride ?? props.eventsListState.events;

        switch (section) {
            case 'inplay': {
                eventsQuery = props.eventsListState.inPlayEvents;
                break;
            }
            case 'outrights': {
                eventsQuery = props.eventsListState.outrightEvents;
                break;
            }
        }

        const events = state.groupEvents.get(eventsQuery).listToViewUnified;

        if (page === 'homepage' || page === 'inplay') {
            return {
                sports: events.sports.filter((ev) => ev.id === props.sportId),
            };
        }

        return events;
    };

    const isFeaturedView =
        page === 'sport' &&
        !props.eventsListState.filter_outrights &&
        !props.eventsListState.filter_inPlay &&
        props.eventsListState.filter_time === null;

    const renderCompetitionsList = (section?: FeaturedSectionType) => {
        const { sports } = getListToView(section);

        return sports.length > 0 && props.eventsListState.isLoading === false ? (
            sports.map((sportGroup) => {
                const competitionNodes = renderSportGroup(
                    sportGroup,
                    props.eventsListState,
                    props.preferCompetitionSelectionsHeadersOnly,
                    page === 'competition'
                );

                return (
                    <SportGroup
                        key={`sport_${sportGroup.id}`}
                        sportGroup={sportGroup}
                        competitionNodes={competitionNodes}
                        showHeaderSport={props.showHeaderSport}
                    />
                );
            })
        ) : (
            <Status
                section={section}
                eventsListState={props.eventsListState}
            />
        );
    };

    const renderFeaturedView = () => {
        const sections: Array<{ id: FeaturedSectionType; label: string }> = [
            { id: FeaturedSectionType.MATCHES, label: isFightSport(props.sportId) ? 'Fights' : 'Matches' },
            { id: FeaturedSectionType.COMPETITIONS, label: isGolf(props.sportId) ? 'Tournaments' : 'Competitions' },
            { id: FeaturedSectionType.INPLAY, label: 'In Play' },
            ...(shouldShowOutrightsFilter(props.sportId)
                ? [{ id: FeaturedSectionType.OUTRIGHTS, label: 'Outrights' }]
                : []),
        ];
        const sportWideListState = appSportsBookState.unifiedSportEventsListState.get(props.sportId).get(null);

        return sections.map(({ id, label }) => {
            const isOutrightsSection = id === FeaturedSectionType.OUTRIGHTS;

            if (
                (id === FeaturedSectionType.INPLAY && sportWideListState.inPlayCount === 0) ||
                (isOutrightsSection && sportWideListState.outrightsCount === 0)
            ) {
                return null;
            }

            if (id === FeaturedSectionType.COMPETITIONS || isOutrightsSection) {
                return (
                    <SportCompetitionsList
                        sportId={props.sportId}
                        sportWideListState={sportWideListState}
                        isOutrights={isOutrightsSection}
                        label={label}
                    />
                );
            }

            return (
                <FeaturedSection
                    data-testid={id}
                    className={id}
                >
                    <SectionHeader
                        title={label}
                        dataTest={`section-header-${id}`}
                    />
                    {renderCompetitionsList(id)}
                </FeaturedSection>
            );
        });
    };

    const { sports } = getListToView() || [];
    const isEmpty = sports.length === 0 && !props.eventsListState.isLoading;

    return (
        <EventListWrapper
            variant={DefaultComponentVariantType.TERTIARY}
            className={classNames({
                'event-list--outrights': props.eventsListState.isOutrightsMarket(),
                'event-list--empty': isEmpty,
            })}
        >
            {isFeaturedView ? renderFeaturedView() : renderCompetitionsList()}
        </EventListWrapper>
    );
});

export const EventsListUnified = observer('EventsListUnified', (props: EventsListUnifiedPropsType) => {
    const filter_inPlay = props.eventsListState.filter_inPlay;
    const filter_outrights = props.eventsListState.filter_outrights;
    const filter_time = props.eventsListState.filter_time ?? 'none';
    const filter_market = props.eventsListState.filter_market ?? 'none';
    const contentKey =
        props.eventsCollectionQueryOverride !== undefined
            ? `unified-mixed--${filter_inPlay}-${filter_outrights}-${filter_time}-${filter_market}`
            : `${filter_inPlay}-${filter_outrights}-${filter_time}-${filter_market}`;

    return (
        <EventsListUnifiedContent
            key={contentKey}
            allGroupsOpen={props.allGroupsOpen}
            eventsCollectionQueryOverride={props.eventsCollectionQueryOverride}
            eventsListState={props.eventsListState}
            groupingSpecialSports={props.groupingSpecialSports}
            preferCompetitionSelectionsHeadersOnly={props.preferCompetitionSelectionsHeadersOnly}
            showHeaderSport={props.showHeaderSport}
            sportId={props.sportId}
        />
    );
});
